import { useState } from "react";
import * as api from "../../api/admin";
import { useAdminTask } from "./useAdminData";
import { downloadFile, number } from "./adminModel";
import {
  Button,
  CheckField,
  ConfirmDialog,
  Dialog,
  Icon,
  Notice,
  SectionHeader,
} from "./components/AdminUI";
import styles from "./admin.module.css";

const IMPORTS = [
  {
    id: "statuses",
    title: "Статусы отбора",
    icon: "users",
    description:
      "Загрузите решения об отборе для текущих и будущих участников сезона.",
    format: "XLSX",
    hint: "Столбец «Статус» и идентификатор: «Почта», «Логин» или «Код участника». Для ещё не зарегистрированных участников обязательно укажите почту — ту же, с которой они создадут аккаунт.",
    effect: "Статусы найденных пользователей обновятся сразу. Строки с почтой сохранятся для автопривязки после регистрации и подтверждения email. Повторно загружать файл для каждого нового аккаунта не нужно.",
    submit: "Обновить статусы",
    run: (files, option) =>
      api.importAdminSelectionStatuses(files[0], { resetMissing: option }),
    option: "Сбросить статусы пользователей, которых нет в файле",
    optionHint: "Также удалит отложенные статусы из предыдущих загрузок. Без этой настройки текущие и отложенные статусы сохранятся.",
  },
  {
    id: "roster",
    title: "Публичный список участников",
    icon: "globe",
    description:
      "Опубликуйте список прошедших отбор участников на странице интенсива.",
    format: "XLSX",
    hint: "Исходная таблица отбора. На сайт попадут ФИО, подразделение, специальность, курс и отметка для проектов со звёздочкой.",
    effect:
      "Публичный список будет заменён. Участники с указанной почтой получат статус автоматически после подтверждения email, если в сезоне ещё нет решения по их аккаунту. Существующие решения и роли сохранятся.",
    submit: "Обновить публичный список",
    run: (files) => api.importAdminSelectionRoster(files[0]),
  },
  {
    id: "teams",
    title: "Формирование команд",
    icon: "teams",
    description:
      "Создайте команды, добавьте участников и наставников, привяжите проекты.",
    format: "XLSX",
    hint: "Таблица с блоками проектов: код, тема, участники, наставники и чат. Капитаном станет первый найденный участник блока.",
    effect:
      "Команды будут созданы или обновлены. Также обновятся их публичные карточки. Ненайденные участники появятся в отчёте.",
    submit: "Сформировать команды",
    run: (files) => api.importAdminTeams(files[0]),
  },
  {
    id: "yufunts",
    title: "Начисление ЮФУнтов",
    icon: "wallet",
    description: "Начислите командные ЮФУнты по итоговым таблицам интенсива.",
    format: "XLSX / ZIP",
    multiple: true,
    hint: "Первая вкладка Excel: «код проекта» или код в названии команды и колонка «Сумма/Всего ЮФУнтов». Можно выбрать несколько файлов или ZIP-архив.",
    effect:
      "Указанные суммы будут начислены командам. Уже импортированные строки будут пропущены.",
    submit: "Начислить ЮФУнты",
    run: (files) => api.importAdminTeamYufunts(files),
  },
  {
    id: "schedule",
    title: "Расписание интенсива",
    icon: "calendar",
    description:
      "Загрузите события и онлайн-встречи по всем трекам из таблицы координаторов.",
    format: "XLSX",
    hint: "Исходная таблица координаторов с треками, датами, событиями и ссылками. Поддерживаются двухстрочные блоки.",
    effect:
      "Всё текущее расписание будет заменено событиями из файла. Проверьте, что загружаете полную актуальную таблицу.",
    submit: "Заменить расписание",
    run: (files) => api.importAdminSchedule(files[0]),
  },
  {
    id: "showcase",
    title: "Кейсы для витрины",
    icon: "cases",
    description:
      "Добавьте новые кейсы или обновите существующие в выбранном сезоне.",
    format: "XLSX",
    hint: "Шаблоны «Партнеры», «Исследования», «Стартапы». Сохраняйте столбец «Время создания» или укажите «Код проекта», чтобы обновлять кейсы даже после переименования. Новые стартапы импортируются скрытыми.",
    effect: "Найденные кейсы обновятся, новые добавятся. Выбор студентов, публикация, трек и порядок существующих кейсов сохранятся. Кейсы, отсутствующие в файле, останутся на сайте.",
    submit: "Импортировать кейсы",
    run: (files) => api.importAdminShowcaseCases(files[0]),
  },
];
const RESULT_LABELS = {
  imported_count: "Загружено записей",
  created_count: "Добавлено",
  updated_count: "Обновлено",
  unchanged_count: "Без изменений",
  not_found_count: "Не найдено",
  pending_count: "Сохранено для автопривязки по почте",
  skipped_count: "Пропущено строк",
  published_count: "Опубликовано участников",
  parsed_team_blocks: "Блоков проектов",
  created_teams: "Создано команд",
  updated_teams: "Обновлено команд",
  skipped_team_blocks: "Пропущено блоков",
  published_team_cards: "Публичных карточек",
  members_added: "Добавлено участников",
  mentors_added: "Добавлено наставников",
  captains_assigned: "Назначено капитанов",
  linked_cases: "Привязано кейсов",
  unresolved_users: "Не найдены участники",
  unresolved_mentors: "Не найдены наставники",
  unresolved_cases: "Не найдены кейсы",
  parsed_rows: "Обработано строк",
  deposited_rows: "Начислено строк",
  skipped_rows: "Пропущено строк",
  not_found_rows: "Не найдены команды",
  ambiguous_rows: "Неоднозначные совпадения",
  duplicate_rows: "Повторные строки",
  total_amount: "Всего начислено ЮФУнтов",
};

function ImportDialog({ config, onClose, onComplete }) {
  const [files, setFiles] = useState([]);
  const [option, setOption] = useState(false);
  const [step, setStep] = useState("file");
  const [result, setResult] = useState(null);
  const task = useAdminTask();
  const accept = config.multiple ? ".xlsx,.zip" : ".xlsx";
  const chooseFiles = (selected) => {
    task.setError("");
    setFiles([]);
    const next = Array.from(selected || []);
    if (!config.multiple && next.length > 1) {
      task.setError("Для этого импорта выберите один файл.");
      return;
    }
    if (
      next.some(
        (file) =>
          !(config.multiple ? /\.(xlsx|zip)$/i : /\.xlsx$/i).test(file.name),
      )
    ) {
      task.setError(`Поддерживаются только файлы ${config.format}.`);
      return;
    }
    if (next.some((file) => file.size === 0)) {
      task.setError(
        "Один из выбранных файлов пуст. Выберите заполненную таблицу.",
      );
      return;
    }
    setFiles(next);
  };
  const effect =
    option && config.id === "statuses"
        ? `${config.effect} Статусы отсутствующих в файле пользователей будут сброшены.`
        : config.effect;
  const issues = result
    ? [
        ...(result.not_found_identifiers || []).map(
          (value) => `Не найден пользователь: ${value}`,
        ),
        ...(result.unresolved_user_examples || []).map(
          (value) => `Не найден участник: ${value}`,
        ),
        ...(result.unresolved_mentor_examples || []).map(
          (value) => `Не найден наставник: ${value}`,
        ),
        ...(result.unresolved_case_codes || []).map(
          (value) => `Не найден кейс: ${value}`,
        ),
        ...(result.results || [])
          .filter(
            (item) => item.status !== "deposited" && item.status !== "skipped",
          )
          .map(
            (item) =>
              `${item.file_name ? `${item.file_name} · ` : ""}${item.project_code || ""}: ${item.reason || "Строка не обработана"}`,
          ),
      ]
    : [];
  return (
    <Dialog
      title={config.title}
      description={
        result
          ? "Результат импорта"
          : "Загрузка файла → проверка действия → результат"
      }
      onClose={onClose}
      busy={task.busy}
      footer={
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            {result ? (
              <Button variant="primary" onClick={onClose}>
                Готово
              </Button>
            ) : (
              <>
                <Button
                  disabled={task.busy}
                  onClick={step === "file" ? onClose : () => setStep("file")}
                >
                  {step === "file" ? "Отмена" : "Назад"}
                </Button>
                <Button
                  variant="primary"
                  disabled={task.busy || !files.length}
                  onClick={() =>
                    step === "file"
                      ? setStep("review")
                      : task.run(
                          () => config.run(files, option),
                          (data) => {
                            setResult(data);
                            onComplete();
                          },
                        )
                  }
                >
                  {task.busy
                    ? "Импортируем…"
                    : step === "file"
                      ? "Продолжить"
                      : config.submit}
                </Button>
              </>
            )}
          </div>
        </>
      }
    >
      {result ? (
        <>
          <Notice>
            Импорт завершён. Проверьте количество обновлённых и пропущенных
            записей.
          </Notice>
          {result.pending_count > 0 && <p className={styles.hint}>
            Решения по почте сохранены. После регистрации и подтверждения этой почты
            статус применится автоматически в выбранном сезоне. Повторно загружать файл не нужно.
            Решения о зачислении, отказе и выходе, а также роли автоматическая сверка сохраняет.
            {typeof result.not_found_count === 'number' && ' Сохранённые адреса входят в число пока не найденных аккаунтов.'}
          </p>}
          <div className={styles.importResult}>
            {Object.entries(RESULT_LABELS)
              .filter(([key]) => typeof result[key] === "number")
              .map(([key, label]) => (
                <div key={key} className={styles.resultMetric}>
                  <strong>{number(result[key])}</strong>
                  <span>{label}</span>
                </div>
              ))}
          </div>
          {result.tracks?.length > 0 && (
            <p className={styles.hint}>Треки: {result.tracks.join(", ")}</p>
          )}
          {issues.length > 0 && (
            <details open>
              <summary>Требуют внимания: {issues.length}</summary>
              <ul className={styles.issueList}>
                {issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : (
        <div className={styles.stack}>
          <div className={styles.stepLabel}>
            <strong>
              {step === "file" ? "01 / Выбор файла" : "02 / Проверка действия"}
            </strong>
            <span>из 2 шагов</span>
          </div>
          {step === "file" ? (
            <>
              <label
                className={styles.fileDrop}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  chooseFiles(event.dataTransfer.files);
                }}
              >
                <Icon name="upload" size={28} />
                <strong>Выберите файл или перетащите сюда</strong>
                <span className={styles.hint}>
                  {config.format}
                  {config.multiple
                    ? " · можно несколько файлов"
                    : " · один файл"}
                </span>
                <input
                  type="file"
                  aria-label="Файл для импорта"
                  accept={accept}
                  multiple={config.multiple}
                  onChange={(event) => chooseFiles(event.target.files)}
                />
              </label>
              <p className={styles.hint}>{config.hint}</p>
              {config.option && (
                <CheckField
                  label={config.option}
                  hint={config.optionHint}
                  checked={option}
                  onChange={(event) => setOption(event.target.checked)}
                />
              )}
            </>
          ) : (
            <div className={styles.helpBanner}>
              <Icon name="alert" />
              <span>{effect}</span>
            </div>
          )}
          {!!files.length && (
            <ul className={styles.fileList}>
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  {file.name}
                  <small>{Math.max(1, Math.round(file.size / 1024))} КБ</small>
                </li>
              ))}
            </ul>
          )}
          {step === "review" && (
            <p className={styles.hint}>
              Данные изменятся после нажатия «{config.submit}». Отчёт появится
              здесь после завершения операции.
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

export function ImportSection({ notify, refreshDashboard, navigate }) {
  const [config, setConfig] = useState(null);
  const [sync, setSync] = useState(false);
  const task = useAdminTask();
  return (
    <>
      <SectionHeader
        title="Импорт и выгрузки"
        description="Массовое обновление данных из Excel. Выберите задачу — покажем формат и порядок действий."
      />
      <div className={styles.importGrid}>
        {IMPORTS.map((item) => (
          <article
            key={item.id}
            className={styles.importCard}
            aria-labelledby={`import-title-${item.id}`}
          >
            <div className={styles.importCardTop}>
              <span>
                <Icon name={item.icon} />
              </span>
              <h2 id={`import-title-${item.id}`}>{item.title}</h2>
            </div>
            <p>{item.description}</p>
            <div className={styles.inlineActions}>
              <span className={styles.hint}>{item.format}</span>
              <Button icon="upload" onClick={() => setConfig(item)}>
                Загрузить файл
              </Button>
            </div>
          </article>
        ))}
      </div>
      <div className={styles.divider} />
      <div className={styles.blockTitle}>
        <h2>Синхронизация и отчёты</h2>
      </div>
      {task.error && (
        <Notice danger onClose={() => task.setError("")}>
          {task.error}
        </Notice>
      )}
      <div className={styles.importGrid}>
        <article className={styles.importCard}>
          <div className={styles.importCardTop}>
            <span>
              <Icon name="refresh" />
            </span>
            <h2>Синхронизировать отбор</h2>
          </div>
          <p>
            Новые аккаунты подхватывают сохранённые решения по подтверждённой почте автоматически.
            Ручная синхронизация обновит статусы существующих участников по публичному списку,
            в том числе найдёт совпадения по логину и ФИО.
          </p>
          <Button onClick={() => setSync(true)}>
            Синхронизировать статусы
          </Button>
        </article>
        <article className={styles.importCard}>
          <div className={styles.importCardTop}>
            <span>
              <Icon name="download" />
            </span>
            <h2>Выгрузить данные</h2>
          </div>
          <p>Составы команд, цифровые следы и выбор кейсов участниками.</p>
          <div className={styles.inlineActions}>
            <Button
              disabled={task.busy}
              onClick={() =>
                task.run(api.exportAdminTeamMembers, (blob) => {
                  downloadFile(blob, "sostavy-komand.xlsx");
                  notify("Составы команд выгружены.");
                })
              }
            >
              Команды · XLSX
            </Button>
            <Button
              disabled={task.busy}
              onClick={() =>
                task.run(api.exportAdminArtifacts, (blob) => {
                  downloadFile(blob, "cifrovye-sledy.xlsx");
                  notify("Цифровые следы выгружены.");
                })
              }
            >
              Цифровые следы
            </Button>
            <Button onClick={() => navigate("priorities")}>Приоритеты →</Button>
          </div>
        </article>
      </div>
      {config && (
        <ImportDialog
          config={config}
          onClose={() => setConfig(null)}
          onComplete={refreshDashboard}
        />
      )}
      {sync && (
        <ConfirmDialog
          title="Синхронизировать статусы отбора?"
          description="Статусы пользователей будут обновлены по текущему публичному списку участников."
          confirmLabel="Синхронизировать"
          onClose={() => setSync(false)}
          onConfirm={async () => {
            const result = await api.syncAdminSelectionRosterToUsers();
            notify(
              `Статусы синхронизированы. Обновлено: ${result.updated_count}. Совпадений: ${result.matched_count}, из них по ФИО: ${result.matched_by_full_name_count}. Не найдены: ${result.not_found_count}.`,
            );
            refreshDashboard();
          }}
        />
      )}
    </>
  );
}
